import { invalidateAll } from '$app/navigation';
import { handleApiError } from '@astlai/common/util/error-handling';
import { pbService } from './pb-service.svelte';
import { COL_USERS } from 'src/constants/collections';

/** Extension of Pocketbase Service. Used on the server and on the client */
export class AuthService {
	async login({ username, password }: { username: string; password: string }): Promise<void> {
		const res = await fetch('/api/sign-in', {
			method: 'POST',
			body: JSON.stringify({ username: username.toLowerCase(), password })
		});
		await handleApiError(res);
		await invalidateAll();
	}

	async loginWithOAuth2(provider: string, tenant?: string): Promise<void> {
		const authData = await pbService.collection(COL_USERS).authWithOAuth2({
			provider
		});

		if (!authData.record) {
			throw new Error('OAuth2 authentication failed');
		}

		// Send the auth token to our custom backend to set the auth cookie
		const res = await fetch('/api/oauth-callback', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			credentials: 'include',
			body: JSON.stringify({
				token: authData.token,
				record: authData.record,
				isNew: authData.meta?.isNew,
				tenant
			})
		});

		await handleApiError(res);

		// Clear PocketBase's localStorage auth - we use cookie auth exclusively
		pbService.authStore.clear();

		await invalidateAll();
	}

	async signup({
		email,
		firstName,
		lastName,
		password,
		tenant
	}: {
		email: string;
		firstName: string;
		lastName: string;
		password: string;
		tenant?: string;
	}): Promise<void> {
		const res = await fetch('/api/signup', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			credentials: 'include',
			body: JSON.stringify({ email: email.toLowerCase(), firstName, lastName, password, tenant })
		});
		await handleApiError(res);
		await invalidateAll();
	}

	async logout() {
		const res = await fetch('/api/logout', {
			method: 'POST',
			credentials: 'include' // Ensure cookies are sent with the request
		});
		await handleApiError(res);
		await invalidateAll();
	}
}

export const authService = new AuthService();
